import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AuthScreen } from './AuthScreen';
import { clearNext, peekNext, useAuth } from '../../lib/auth';

/** Reads `error_description` / `error_code` that Supabase puts in the hash or query on a failed link. */
function linkError(): string | null {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(window.location.search);
  const code = params.get('error_code') ?? query.get('error_code');
  const desc = params.get('error_description') ?? query.get('error_description');
  if (!code && !desc && !params.get('error') && !query.get('error')) return null;
  if (code === 'otp_expired') return 'The link has expired or was already used. Links work once, for an hour.';
  return desc ? desc.replace(/\+/g, ' ') : 'The link could not be used.';
}

/**
 * Where the magic link lands. supabase-js (`detectSessionInUrl`) reads the session from the URL
 * on load; once it is there this page moves on to the app.
 */
export default function AuthCallback() {
  const { session } = useAuth();
  const err = useMemo(linkError, []);
  const [next] = useState(peekNext);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (session) clearNext();
  }, [session]);

  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(t);
  }, []);

  if (session) return <Navigate to={next} replace />;

  if (err || (slow && session === null)) {
    return (
      <AuthScreen title="This sign-in link did not work">
        <p>{err ?? 'No session came back from the link.'}</p>
        <Link className="btn primary" to="/login">
          Send a new link
        </Link>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen title="Signing you in">
      <p role="status">One moment.</p>
    </AuthScreen>
  );
}
